import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ILanguageInfo, LanguageService } from '@app/core/services/language.service';
import { ModalService } from '@app/core/services/modal.service';
import { ProductSettingItem, ProductSettingMcItem, ProductSettingMcsInput, ProductSettingService } from '@app/services/product-setting.service';
import { ProductGet } from '@app/services/product.service';
import { forkJoin, from, ReplaySubject, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'tt-product-pim-form',
    templateUrl: './product-pim-form.component.html',
    styleUrls: ['./product-pim-form.component.css'],
})
export class ProductPimFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public set params(value: ProductGet | undefined) {
        this.setParams(value);
    }
    public get params() {
        return this._params;
    }
    private _params?: ProductGet;

    @Output()
    public paramsChange = new EventEmitter<ProductGet>();

    private setParams(value: ProductGet | undefined) {
        this._params = value;

        if (!!value) {
            this.params$.next(value);
        }
    }

    private destroy$ = new Subject<void>();

    private params$ = new ReplaySubject<ProductGet>(1);

    public productSettings: ProductSettingItem[] = [];

    public languages: ILanguageInfo[] = [];

    public pimFields: ProductSettingMcsInput[] = [];

    public pimFieldsItems: { [key: string]: ProductSettingMcItem[] } = {};

    public savingPimData = false;

    constructor(
        private productSetting: ProductSettingService,
        private language: LanguageService,
        private modal: ModalService,
        private cdr: ChangeDetectorRef,
    ) {}

    public async savePimData() {
        if (!this.params) return;

        try {
            this.savingPimData = true;
            this.cdr.markForCheck();

            const { errorcode, errormessage } = await this.productSetting.saveProductPimData({ ...this.params, metacolumns: this.pimFields });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.paramsChange.next(this.params);
                this.setParams(this.params);
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.savingPimData = false;
            this.cdr.markForCheck();
        }
        //
    }

    public onChanged(key: keyof ProductGet) {
        if (!this.params) return;

        this.params$.next(this.params);
        this.paramsChange.next(this.params);
    }

    private loadStaticData() {
        forkJoin({
            productSettings: from(this.productSetting.getProductSettings()),
            languages: from(this.language.List()),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                this.productSettings = result.productSettings;
                this.languages = result.languages;
                this.cdr.markForCheck();
            });
    }

    private setupParamsSubscriptions() {
        this.params$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.pimFields = [])),
                switchMap((params) => from(this.productSetting.getProductPimFields(params))),
            )
            .subscribe((pimFields) => {
                this.pimFields = pimFields;
                this.cdr.markForCheck();
            });

        this.params$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.pimFieldsItems = {})),
                switchMap((params) => from(this.productSetting.getPimFieldsItems(params))),
            )
            .subscribe((pimFieldsItems) => {
                this.pimFieldsItems = pimFieldsItems;
                this.cdr.markForCheck();
            });
    }

    ngOnInit(): void {
        this.loadStaticData();
        this.setupParamsSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']) {
            this.params = changes['params'].currentValue;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
